/**
 * Centralized native app detection utilities.
 * All webview/platform detection should use these functions.
 */

/** Check if running inside the Carvana native app webview */
export const isCarvanaNativeApp = () => /CarvanaNativeWebView/i.test(navigator?.userAgent);

/** Alias for isCarvanaNativeApp */
export const isNativeWebview = () => isCarvanaNativeApp();

/** Check if running on Android */
export const isAndroid = () => /Android/i.test(navigator?.userAgent);

/** Check if running inside the Carvana native app webview on Android */
export const isAndroidNativeWebview = () => isCarvanaNativeApp() && isAndroid();

/** Check if running inside the Carvana native app webview on iOS */
export const isIOSNativeWebview = () => isCarvanaNativeApp() && !isAndroid();

/** Get the native app version from user agent (e.g., "5.40.0") */
export const getNativeAppVersion = (): string | null => {
  const match = navigator?.userAgent.match(/CarvanaNativeWebView\/([\d.]+)/i);
  return match ? match[1] : null;
};

const isVersionGreaterThanOrEqualTo = (targetVersion: string): boolean => {
  const currentVersion = getNativeAppVersion();
  if (!currentVersion) {
    return false;
  }
  const currentParts = currentVersion.split('.').map(part => {
    const num = parseInt(part, 10);
    return isNaN(num) ? 0 : num;
  });
  const targetParts = targetVersion.split('.').map(Number);

  for (let i = 0; i < targetParts.length; i++) {
    if ((currentParts[i] || 0) > targetParts[i]) return true;
    if ((currentParts[i] || 0) < targetParts[i]) return false;
  }
  return true;
};

export const canHandleBackNavigation = (): boolean => {
  if (isAndroidNativeWebview()) {
    return isVersionGreaterThanOrEqualTo('6.50.0');
  } else {
    return isVersionGreaterThanOrEqualTo('6.67.0');
  }
};

const ANDROID_MIN_CHAT_PAGE_CONTEXT_VERSION = '7.23.0';
const IOS_MIN_CHAT_PAGE_CONTEXT_VERSION = '7.24.0';

export const canHandleChatPageContext = (): boolean => {
  if (isAndroidNativeWebview()) {
    return isVersionGreaterThanOrEqualTo(ANDROID_MIN_CHAT_PAGE_CONTEXT_VERSION);
  }
  if (isIOSNativeWebview()) {
    return isVersionGreaterThanOrEqualTo(IOS_MIN_CHAT_PAGE_CONTEXT_VERSION);
  }
  return false;
};
